import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';

export interface BarcodeMapping {
  id: string;
  barcode_value: string;
  asset_id: string;
  interaction_type_id: string;
  asset_instance_id: string;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
}

export interface CreateBarcodeMappingParams {
  barcode_value: string;
  asset_id: string;
  interaction_type_id: string;
  asset_instance_id?: string;
}

const BARCODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const BARCODE_LENGTH = 8;
const SHORT_CODE_PATTERN = /^[A-Z0-9]{8}$/;

/** True for values in the short-code format used on barcodes and NFC tags (e.g. "A1B2C3D4"). */
export function isShortCode(value: string | null | undefined): value is string {
  return !!value && SHORT_CODE_PATTERN.test(value);
}

@Injectable({ providedIn: 'root' })
export class BarcodeMappingService {
  private db = inject(DatabaseService);
  private authService = inject(AuthService);
  private logger = inject(LoggerService).getLogger('BarcodeMappingService');

  private parseRow(row: any): BarcodeMapping {
    return {
      id: row.id,
      barcode_value: row.barcode_value,
      asset_id: row.asset_id,
      interaction_type_id: row.interaction_type_id,
      asset_instance_id: row.asset_instance_id,
      created_by: row.created_by,
      created_at: row.created_at,
      updated_by: row.updated_by,
      updated_at: row.updated_at,
    };
  }

  async getByBarcodeValue(value: string): Promise<BarcodeMapping | null> {
    await this.db.init();
    const row = await this.db.get('SELECT * FROM barcode_mappings WHERE barcode_value = ?', [
      value,
    ]);
    return row ? this.parseRow(row) : null;
  }

  async getByTarget(
    assetId: string,
    interactionTypeId: string,
    assetInstanceId: string = '',
  ): Promise<BarcodeMapping | null> {
    await this.db.init();
    const row = await this.db.get(
      'SELECT * FROM barcode_mappings WHERE asset_id = ? AND interaction_type_id = ? AND asset_instance_id = ?',
      [assetId, interactionTypeId, assetInstanceId],
    );
    return row ? this.parseRow(row) : null;
  }

  async create(params: CreateBarcodeMappingParams): Promise<BarcodeMapping> {
    await this.db.init();
    const id = this.db.generateId();
    const userId = this.authService.getCurrentUserId() || '';
    const now = Date.now();
    const barcodeValue = await this.generateUniqueBarcodeValue();

    await this.db.run(
      `INSERT INTO barcode_mappings (id, barcode_value, asset_id, interaction_type_id, asset_instance_id, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        barcodeValue,
        params.asset_id,
        params.interaction_type_id,
        params.asset_instance_id || '',
        userId,
        now,
        userId,
        now,
      ],
    );

    this.db.notifyDataChange('barcode_mappings');

    const mapping = await this.getById(id);
    if (!mapping) throw new Error('Failed to create barcode mapping');
    return mapping;
  }

  async getOrCreateForTarget(
    assetId: string,
    interactionTypeId: string,
    assetInstanceId: string = '',
  ): Promise<BarcodeMapping> {
    const existing = await this.getByTarget(assetId, interactionTypeId, assetInstanceId);
    if (existing) return existing;

    return this.create({
      barcode_value: '', // will be generated in create()
      asset_id: assetId,
      interaction_type_id: interactionTypeId,
      asset_instance_id: assetInstanceId,
    });
  }

  async getById(id: string): Promise<BarcodeMapping | null> {
    await this.db.init();
    const row = await this.db.get('SELECT * FROM barcode_mappings WHERE id = ?', [id]);
    return row ? this.parseRow(row) : null;
  }

  private async generateUniqueBarcodeValue(): Promise<string> {
    const maxAttempts = 10;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const code = this.generateRandomCode();
      const existing = await this.getByBarcodeValue(code);
      if (!existing) return code;
      this.logger.debug('Barcode value collision, retrying:', code);
    }
    throw new Error('Failed to generate unique barcode value after maximum attempts');
  }

  private generateRandomCode(): string {
    const array = new Uint8Array(BARCODE_LENGTH);
    crypto.getRandomValues(array);
    let code = '';
    for (let i = 0; i < BARCODE_LENGTH; i++) {
      code += BARCODE_CHARS[array[i] % BARCODE_CHARS.length];
    }
    return code;
  }
}
